import { logger as baseLogger } from "matrix-js-sdk/src/logger";
import { IDthContact } from "../dth/clientHttp/models/httpClientModels";
import { DthHttpClient } from "../dth/clientHttp/DthHttpClient";
import { DthSessionStorage } from "../dth/clientHttp/DthSessionStorage";
import { API_CONTACTS, API_MATRIX_NAME_KEY, API_USER_ID_KEY } from "../dth/constants";
import CustomError from "../dth/clientHttp/errors/CustomError";
import { ContactFormatEnum } from "../dth/ContactFormatEnum";
import { getContactFormatOrDefault, getContactNameFormat } from "../dth/contactFormatUtils";
import { getDomainFromMatrixId, participantIdToMatrixId } from "../dth/clientHttp/utils/utils";

const logger = baseLogger.getChild("ContactStateManager");

/**
 * Contact State Event 
 * Tipo di evento per notificare i listener del ContactStateManager. 
 * Può essere 
 *  - "update" quando i contatti vengono aggiornati
 *  - "reset" quando lo stato viene resettato
 */
export enum ContactStateEvent {
    Update = "update",
    Reset = "reset"
}
/**
 * Contact State Listener
 * Tipo di funzione che può essere registrata come listener per gli eventi di stato dei contatti.
 * Riceve un evento di tipo ContactStateEvent quando viene notificato un cambiamento nello stato dei contatti.
 */
type ContactStateListener = (event: ContactStateEvent) => void;

/**
 * ContactStateManager
 * Gestisce lo stato centralizzato dei contatti utente.
 * Mantiene una mappa aggiornata (matrixName -> contatto) e si occupa di sincronizzare i dati con il server.
 * Tutta la logica di accesso, aggiornamento e fetch dei contatti passa da questa classe.
 */
export class ContactStateManager {
    //#region Listeners
    /**
     * Lista dei listener registrati per eventi di stato contatti.
     */
    private static _listeners: Set<ContactStateListener> = new Set();
    /**
     * Registra un listener per eventi di aggiornamento/reset dei contatti.
     * @param listener Funzione da chiamare quando lo stato cambia.
     */
    public static addListener(listener: ContactStateListener): void {
        ContactStateManager._listeners.add(listener);
    }

    /**
     * Rimuove un listener precedentemente registrato.
     * @param listener Funzione listener da rimuovere.
     */
    public static removeListener(listener: ContactStateListener): void {
        ContactStateManager._listeners.delete(listener);
    }

    /**
     * Notifica tutti i listener di un evento.
     * @param event Tipo di evento ("update" o "reset").
     */
    private static notify(event: ContactStateEvent): void {
        for (const listener of ContactStateManager._listeners) {
            try {
                listener(event);
            } catch (e) {
                logger.error("ContactStateManager listener error", e);
            }
        }
    }
    //#endregion

    /**
     * Mappa dei contatti: chiave matrixName, valore oggetto contatto.
     * Viene aggiornata tramite updateContacts o fetch dal server.
     */
    private static _contactMap: Map<string, IDthContact> = new Map();
    /**
     * Flag che indica se è già stato fatto un tentativo di refresh automatico iniziale.
     */
    private static _triedInitialRefresh = false;

    //#region Private methods

    /**
     * Garantisce che la mappa dei contatti sia caricata da session storage se vuota.
     */
    private static _ensureContactMapLoaded(): void {
        if (ContactStateManager._contactMap.size === 0 && !ContactStateManager._triedInitialRefresh) {
            ContactStateManager._tryRefreshContactsFromSessionStorage();
        }
    }

    /**
     * Funzione interna per ottenere la mappa dei contatti, con logica di refresh e caricamento da sessione.
     * @param refresh Se true, forza l'aggiornamento dal server.
     * @returns Mappa dei contatti aggiornata.
     */
    private static async _getInnerContactMapAsync(refresh: boolean = false): Promise<Map<string, IDthContact>> {
        // Se la mappa è vuota e non abbiamo già tentato un refresh iniziale, proviamo a caricare i contatti da sessione
        ContactStateManager._ensureContactMapLoaded();

        // Se refresh è true, aggiorniamo i contatti dal server
        if (refresh) {
            await ContactStateManager.refreshContactsFromServerAsync();
        }

        return ContactStateManager._contactMap;
    }

    /**
     * Popola la mappa interna dei contatti dall'array fornito.
     * Non persiste su storage e non notifica i listener.
     * @param contacts Array di contatti da inserire nella mappa.
     */
    private static _buildContactMapFromArray(contacts: IDthContact[]): void {
        ContactStateManager._contactMap.clear();
        for (const contact of contacts) {
            ContactStateManager._contactMap.set(contact.matrixName, contact);
        }
    }

    /**
     * Prova ad aggiornare i contatti prelevandoli dalla memoria di sessione.
     * Se presenti, popola la mappa e notifica i listener.
     * Non salva nuovamente su storage (i dati arrivano già da lì).
     * Imposta _triedInitialRefresh = true per evitare tentativi ripetuti.
     * @returns true se i contatti sono stati caricati da sessione, false altrimenti.
     */
    private static _tryRefreshContactsFromSessionStorage(): boolean {
        // Segna subito il tentativo per evitare re-entrant / retry infiniti
        ContactStateManager._triedInitialRefresh = true;
        let savedContacts: IDthContact[] | null;
        try {
            savedContacts = ContactStateManager._getContactsFromSessionStorage();
        } catch (err) {
            logger.warn("Errore durante il caricamento dei contatti da session storage:", err);
            savedContacts = null;
        }

        if (savedContacts == null) return false;
        // Popola la mappa senza re-salvare su storage (già è salvata)
        ContactStateManager._buildContactMapFromArray(savedContacts);
        // Notifica i listener che i contatti sono stati aggiornati (caricati da sessione)
        ContactStateManager.notify(ContactStateEvent.Update);
        return true;
    }

    /**
     * Helper interno per risolvere l'email dato un matrixName,
     * con fallback opzionale sull'utente corrente.
     * @param matrixName Matrix user name del contatto.
     * @param includeCurrentUser Se true, include anche l'utente corrente nella ricerca.
     * @returns Email trovata o undefined.
     */
    private static _resolveEmail(matrixName: string, includeCurrentUser: boolean): string | undefined {
        const contact = ContactStateManager._contactMap.get(matrixName);
        if (contact) return contact.email;
        if (includeCurrentUser) {
            const currentUser = DthSessionStorage.getCurrentUserInfo();
            if (currentUser?.matrixName === matrixName) return currentUser.email;
        }
        return undefined;
    }

    /**
     * Funzione di ordinamento per i contatti, basata sul formato di visualizzazione (Nome Cognome o Cognome Nome).
     * @param firstContact Primo contatto da confrontare.
     * @param secondContact Secondo contatto da confrontare.
     * @param contactFormat Formato di visualizzazione dei contatti.
     * @returns Numero negativo, zero o positivo in base all'ordinamento.
     */
    private static _orderBy(firstContact: IDthContact, secondContact: IDthContact, contactFormat: ContactFormatEnum | null = null): number {
        // se non specificato, prendo il formato dai settings
        const format = getContactFormatOrDefault(contactFormat);
        return (format === ContactFormatEnum.NameSurname)
            ? firstContact.firstName.localeCompare(secondContact.firstName)
            : firstContact.lastName.localeCompare(secondContact.lastName);
    }

    /**
     * Salva i contatti dell'utente nella sessione.
     * @param contacts I contatti dell'utente da salvare nella sessione.
     */
    private static _saveContactsInSessionStorage(contacts: IDthContact[]): void {
        DthSessionStorage.setGenericItem(API_CONTACTS, JSON.stringify(contacts));
    }

    /**
     * Recupera i contatti dell'utente dalla sessione.
     * @returns I contatti dell'utente, o null se non sono disponibili o in caso di errore nel recupero/parsing.
    */
    private static _getContactsFromSessionStorage(): IDthContact[] | null {
        try {
            const contactsJSON = DthSessionStorage.getGenericItem(API_CONTACTS);
            if (!contactsJSON) return null;
            return JSON.parse(contactsJSON);
        } catch (error) {
            logger.error("Errore nel recupero delle info dei contatti:", error);
            return null;
        }
    }
    //#endregion

    //#region public methods

    /**
     * Restituisce la mappa dei contatti (matrixName -> contatto).
     * Se la mappa è vuota, la aggiorna prelevando i dati dal server.
     * @param {boolean} refresh - Se true, forza il fetch dal server.
     * @returns {Promise<Map<string, IDthContact>>} Mappa dei contatti.
     */
    public static async getContactMapAsync(refresh: boolean = false): Promise<Map<string, IDthContact>> {
        const map = await ContactStateManager._getInnerContactMapAsync(refresh);
        // restituisce una copia per evitare modifiche esterne alla mappa interna
        return new Map(map);
    }

    /**
     * Restituisce la mappa dei contatti (matrixName -> contatto).
     * Se la mappa è vuota, tenta il caricamento da session storage.
     * @returns {Map<string, IDthContact>} Mappa dei contatti.
     */
    public static getContactMap(): Map<string, IDthContact> {
        ContactStateManager._ensureContactMapLoaded();
        // restituisce una copia per evitare modifiche esterne alla mappa interna
        return new Map(ContactStateManager._contactMap);
    }

    /**
     * Restituisce un array dei contatti.
     * Se refresh=true, aggiorna i dati dal server prima di restituirli.
     * @param {boolean} refresh - Se true, forza il fetch dal server.
     * @returns {Promise<IDthContact[]>} Array dei contatti.
     */
    public static async getContactsAsync(
        refresh: boolean = false,
        contactFormat: ContactFormatEnum | null = null
    ): Promise<IDthContact[]> {
        const map = await ContactStateManager._getInnerContactMapAsync(refresh);
        const contactFormatValue = getContactFormatOrDefault(contactFormat);
        return Array.from(map.values()).sort((a, b) => ContactStateManager._orderBy(a, b, contactFormatValue));
    }

    /**
     * Restituisce un array dei contatti.
     * Se la mappa è vuota, tenta il caricamento da session storage.
     * @returns {IDthContact[]} Array dei contatti.
     */
    public static getContacts(contactFormat: ContactFormatEnum | null = null): IDthContact[] {
        ContactStateManager._ensureContactMapLoaded();
        const contactFormatValue = getContactFormatOrDefault(contactFormat);
        return Array.from(ContactStateManager._contactMap.values()).sort((a, b) => ContactStateManager._orderBy(a, b, contactFormatValue));
    }

    /**
     * Aggiorna la mappa dei contatti con nuovi dati.
     * Sovrascrive completamente la mappa, persiste su storage e notifica i listener.
     * @param {IDthContact[]} newContacts - Array di contatti da inserire.
     */
    public static updateContacts(newContacts: IDthContact[]): void {
        ContactStateManager._buildContactMapFromArray(newContacts);
        ContactStateManager._saveContactsInSessionStorage(newContacts);
        ContactStateManager.notify(ContactStateEvent.Update);
    }

    /**
     * Trova un contatto tramite matrixName.
     * Se la mappa è vuota, la aggiorna dal server.
     * @param {string} matrixName - Matrix user name del contatto.
     * @param {boolean} refresh - Se true, forza il fetch dal server.
     * @param {boolean} includeCurrentUser - Se true, include anche l'utente corrente nella ricerca (utile se vogliamo mostrare le info anche per il proprio contatto).
     * @returns {Promise<IDthContact | undefined>} Contatto trovato o undefined.
     */
    public static async getContactByMatrixNameAsync(matrixName: string, refresh: boolean = false, includeCurrentUser: boolean = true): Promise<IDthContact | undefined> {
        const map = await ContactStateManager._getInnerContactMapAsync(refresh);
        const contact = map.get(matrixName);
        if (!contact && includeCurrentUser) {
            const currentUserContact = ContactStateManager.getCurrentUserContact();
            if (currentUserContact && currentUserContact.matrixName === matrixName) {
                return currentUserContact;
            }
        }
        return contact;
    }

    /**
     * Restituisce l'email di un contatto dato il suo matrixName.
     * @param {string} matrixName - Matrix user name del contatto.
     * @param {boolean} refresh - Se true, forza il fetch dal server.
     * @param {boolean} includeCurrentUser - Se true, include anche l'utente corrente nella ricerca (utile se vogliamo mostrare l'email anche per il proprio contatto).
     * @returns {Promise<string | undefined>} Email del contatto o undefined.
     */
    public static async getContactEmailByMatrixNameAsync(matrixName: string, refresh: boolean = false, includeCurrentUser: boolean = true): Promise<string | undefined> {
        await ContactStateManager._getInnerContactMapAsync(refresh);
        return ContactStateManager._resolveEmail(matrixName, includeCurrentUser);
    }

    /**
     * Trova un contatto tramite matrixName usando i contatti attualmente caricati.
     * Se la mappa è vuota, tenta il caricamento da session storage.
     * @param {string} matrixName - Matrix user name del contatto.
     * @param {boolean} includeCurrentUser - Se true, include anche l'utente corrente nella ricerca (utile se vogliamo mostrare le info anche per il proprio contatto).
     * @returns {IDthContact | undefined} Contatto trovato o undefined.
     */
    public static getContactByMatrixName(matrixName: string, includeCurrentUser: boolean = true): IDthContact | undefined {
        ContactStateManager._ensureContactMapLoaded();
        const contact = ContactStateManager._contactMap.get(matrixName);
        // Se non troviamo il contatto nella mappa, e includeCurrentUser è true, 
        // proviamo a vedere se è il contatto dell'utente corrente (utile per mostrare le proprie info anche se non sono state caricate come contatti)
        if (!contact && includeCurrentUser) {
            const currentUserContact = ContactStateManager.getCurrentUserContact();
            if (currentUserContact && currentUserContact.matrixName === matrixName) {
                return currentUserContact;
            }
        }
        return contact;
    }

    /**
     * Restituisce l'email di un contatto dato il suo matrixName usando i contatti attualmente caricati.
     * Se la mappa è vuota, tenta il caricamento da session storage.
     * @param {string} matrixName - Matrix user name del contatto.
     * @param {boolean} includeCurrentUser - Se true, include anche l'utente corrente nella ricerca (utile se vogliamo mostrare l'email anche per il proprio contatto).
     * @returns {string | undefined} Email del contatto o undefined.
     */
    public static getContactEmailByMatrixName(matrixName: string, includeCurrentUser: boolean = true): string | undefined {
        if (ContactStateManager._contactMap.size === 0 && !ContactStateManager._triedInitialRefresh) {
            ContactStateManager._tryRefreshContactsFromSessionStorage();
        }
        return ContactStateManager._resolveEmail(matrixName, includeCurrentUser);
    }

    /**
     * Restituisce una stringa con i nomi dei contatti formattati in base alle preferenze dell'utente,
     * separati da virgola, o una stringa vuota se la lista dei contatti è vuota o non definita.
     * @param contacts La lista dei contatti da formattare.
     * @param format Il formato di visualizzazione preferito.
     * @param limit Il numero massimo di contatti da includere nella stringa (opzionale, default: 3). 
     *      Se il numero di contatti supera il limite, viene aggiunto "..." alla fine della stringa.
     *      Se null o <0, non viene applicato alcun limite.
     * @returns Una stringa con i nomi dei contatti formattati.
     */
    public static getContactsNameFormat(
        contacts: IDthContact[] | undefined,
        format: ContactFormatEnum | null,
        limit: number | null = 3
    ): string {
        if (!contacts || contacts.length === 0) return "";

        const currentContactFormat = getContactFormatOrDefault(format);
        const formattedContacts: string[] = contacts.sort((a, b) => ContactStateManager._orderBy(a, b, currentContactFormat))
            .map(contact => getContactNameFormat(contact, currentContactFormat));
        if (limit != null && limit > 0 && formattedContacts.length > limit) {
            return formattedContacts.slice(0, limit).join(", ") + "...";
        }
        return formattedContacts.join(", ");
    }

    /**
     * Restituisce una stringa con i nomi dei contatti formattati in base alle preferenze dell'utente,
     * separati da virgola, o una stringa vuota se la lista dei contatti è vuota o non definita.
     * @param matrixIds La lista dei matrix IDs dei contatti da formattare.
     * @param format Il formato di visualizzazione preferito.
     * @param limit Il numero massimo di contatti da includere nella stringa (opzionale, default: 3). 
     *      Se il numero di contatti supera il limite, viene aggiunto "..." alla fine della stringa.
     *      Se null o <0, non viene applicato alcun limite.
     * @param includeCurrentUser Se true, include anche l'utente corrente nella ricerca (utile se vogliamo mostrare i nomi anche per il proprio contatto).
     * @returns Una stringa con i nomi dei contatti formattati.
     */
    public static getContactsNameFormatByMatrixIds(
        matrixIds: Iterable<string> | undefined,
        format: ContactFormatEnum | null,
        limit: number | null = 3,
        includeCurrentUser: boolean = true
    ): string {
        if (!matrixIds) return "";

        // Per ogni matrix ID, prova a trovare il contatto associato
        const contacts: IDthContact[] = [];
        for (const id of matrixIds) {
            // Prova a trovare un contatto con quel matrix name e, se lo trovi, aggiungilo alla lista dei contatti da formattare
            const contact: IDthContact | undefined = ContactStateManager.getContactByMatrixName(id, includeCurrentUser);
            if (contact) {
                contacts.push(contact);
            }
            // Se è stato specificato un limite e abbiamo già raggiunto il numero massimo di contatti da includere, 
            // interrompi il ciclo
            if (limit != null && limit > 0 && contacts.length > limit) {
                break;
            }
        }
        return ContactStateManager.getContactsNameFormat(contacts, format, limit);
    }

    /**
     * Restituisce una stringa con i nomi dei contatti formattati in base alle preferenze dell'utente,
     * separati da virgola, o una stringa vuota se la lista dei contatti è vuota o non definita.
     * @param participantIds La lista dei participant IDs dei contatti da formattare. (solo username senza dominio, es: "username" per "@username:domain.com")
     * @param format Il formato di visualizzazione preferito.
     * @param limit Il numero massimo di contatti da includere nella stringa (opzionale, default: 3). 
     *      Se il numero di contatti supera il limite, viene aggiunto "..." alla fine della stringa.
     *      Se null o <0, non viene applicato alcun limite.
     * @param includeCurrentUser Se true, include anche l'utente corrente nella ricerca (utile se vogliamo mostrare i nomi anche per il proprio contatto).
     * @returns Una stringa con i nomi dei contatti formattati.
     */
    public static getContactsNameFormatByParticipantIds(
        participantIds: Iterable<string> | undefined,
        format: ContactFormatEnum | null,
        limit: number | null = 3,
        includeCurrentUser: boolean = true
    ): string {
        if (!participantIds) return "";

        // Per convertire i participant IDs in matrix IDs, abbiamo bisogno del dominio dell'utente corrente
        const currentUserMatrixName: string | null = DthSessionStorage.getGenericItem(API_MATRIX_NAME_KEY);
        // Se non abbiamo il matrix name dell'utente corrente, non possiamo convertire i participant IDs
        // Nel caso negativo ritorna una stringa vuota
        const currentMatrixDomain: string = getDomainFromMatrixId(currentUserMatrixName || "");

        // Converte i participant IDs in matrix IDs usando il dominio dell'utente corrente, se disponibile
        // Se non abbiamo il dominio, usiamo i participant IDs così come sono, 
        // ma è probabile che non troveremo corrispondenza con i contatti
        const matrixIds: string[] = currentMatrixDomain ?
            Array.from(participantIds).map(id => participantIdToMatrixId(id, currentMatrixDomain)) :
            Array.from(participantIds);
        // Ora possiamo ottenere i nomi formattati dei contatti usando i matrix IDs convertiti
        return ContactStateManager.getContactsNameFormatByMatrixIds(matrixIds, format, limit, includeCurrentUser);
    }

    /**
     * Restituisce il contatto dell'utente corrente, se disponibile, altrimenti undefined.
     * @returns {IDthContact | undefined} Contatto dell'utente corrente o undefined se non disponibile.
     */
    public static getCurrentUserContact(): IDthContact | undefined {
        const currentUser = DthSessionStorage.getCurrentUserInfo();
        if (!currentUser) return undefined;
        return {
            username: currentUser.username,
            matrixName: currentUser.matrixName,
            phone: currentUser.phone,
            email: currentUser.email,
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
        } as IDthContact;
    }

    /**
     * Preleva i contatti dal server e aggiorna la mappa.
     * Usa l'API user ID salvato in sessione.
     * Se l'ID non è disponibile, mostra un warning e non aggiorna.
     * @param {string} [apiUserId] - (opzionale) API user ID da usare per il fetch. Se non fornito, viene preso da sessione.
     * @returns {Promise<boolean>} True se l'aggiornamento è riuscito, false altrimenti.
     */
    public static async refreshContactsFromServerAsync(apiUserId?: string): Promise<boolean> {
        try {
            const userId = apiUserId || DthSessionStorage.getGenericItem(API_USER_ID_KEY);
            if (!userId) {
                throw new CustomError("API user ID non trovato. Impossibile aggiornare i contatti.");
            }
            const contacts = await DthHttpClient.getInstance().loadContacts({ apiUserId: userId });
            ContactStateManager.updateContacts(contacts);
            return true;
        } catch (err) {
            logger.error("Errore in refreshContactsFromServer:", err);
            return false;
        }
    }

    /**
     * Resetta la mappa dei contatti, svuotando lo stato.
     */
    public static reset(): void {
        ContactStateManager._contactMap.clear();
        ContactStateManager._triedInitialRefresh = false;
        ContactStateManager.notify(ContactStateEvent.Reset);
    }

    //#endregion
}